import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { isDynamicVariable } from '../../utils/dynamicVariables';
import VariableEditorPopup from './VariableEditorPopup';
import { lookupVariable } from '../../utils/variableDisplay';
import { openFolderVariablesFor } from '../../utils/openFolderVariables';

const VariableHighlighter = ({
  text,
  selectedEnvironment,
  globalVariables,
  collectionVariables,
  folderVariables,
  workspaceVariables,
  onVariableUpdate,
  variableContext,
  className = '',
}) => {
  const [tooltip, setTooltip] = useState(null);
  const [editingVariable, setEditingVariable] = useState(null);

  // THE order (utils/variablePrecedence), via the shared display lookup.
  const getVariableValue = (name) => lookupVariable(name, {
    folder: folderVariables,
    collection: collectionVariables,
    environment: selectedEnvironment?.variables,
    workspace: workspaceVariables,
    global: globalVariables,
  });

  const handleVariableClick = (e, name, variableData) => {
    e.preventDefault();
    e.stopPropagation();
    // Folder values are edited in the Folder variables modal; the popup has no
    // folder scope and an edit there would be shadowed (D2).
    if (variableData?.type === 'folder') {
      setTooltip(null);
      openFolderVariablesFor(variableData, variableContext);
      return;
    }
    const defaultScope =
      variableData?.type ||
      (variableContext?.collectionId ? 'collection' : null) ||
      (selectedEnvironment ? 'environment' : null) ||
      (variableContext?.workspaceId ? 'workspace' : null) ||
      'global';
    setEditingVariable({
      name,
      value: variableData?.value || '',
      type: defaultScope,
      exists: !!variableData,
      x: e.clientX,
      y: e.clientY,
    });
    setTooltip(null);
  };

  const handleSave = async (newValue, scope) => {
    if (!editingVariable) return;
    const newVariable = { key: editingVariable.name, value: newValue, enabled: true };
    if (onVariableUpdate) {
      await onVariableUpdate(newVariable, {
        scope,
        collectionId: variableContext?.collectionId,
        workspaceId: variableContext?.workspaceId,
      });
    } else {
      window.dispatchEvent(
        new CustomEvent('updateEnvironmentVariable', {
          detail: { variable: newVariable, environment: selectedEnvironment },
        })
      );
    }
    setEditingVariable(null);
  };

  const renderTextWithVariables = (inputText) => {
    if (!inputText || typeof inputText !== 'string') return inputText;

    const varRegex = /{{\s*([^}]+)\s*}}/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = varRegex.exec(inputText)) !== null) {
      const [fullMatch, rawName] = match;
      const name = rawName.trim();
      const isDynamic = isDynamicVariable(name);
      const variableData = isDynamic ? { value: 'Computed at request time', type: 'dynamic' } : getVariableValue(name);
      const exists = isDynamic || !!variableData;

      if (match.index > lastIndex) {
        parts.push(inputText.substring(lastIndex, match.index));
      }

      parts.push(
        <span
          key={`var-${match.index}`}
          className={`variable-span ${exists ? 'variable-span--exists' : 'variable-span--missing'}`}
          onMouseEnter={(e) =>
            setTooltip({ name, value: variableData?.display ?? variableData?.value ?? '', type: variableData?.type ?? '', exists, x: e.clientX, y: e.clientY })
          }
          onMouseMove={(e) => setTooltip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : null)}
          onMouseLeave={() => setTooltip(null)}
          onClick={(e) => !isDynamic && handleVariableClick(e, name, variableData)}
        >
          {fullMatch}
        </span>
      );

      lastIndex = match.index + fullMatch.length;
    }

    if (lastIndex < inputText.length) {
      parts.push(inputText.substring(lastIndex));
    }

    return parts.length > 1 ? parts : inputText;
  };

  const tooltipEl = tooltip && !editingVariable && ReactDOM.createPortal(
    <div
      className="variable-tooltip fixed pointer-events-none"
      style={{ left: tooltip.x + 14, top: tooltip.y + 16, zIndex: 99999 }}
    >
      {tooltip.exists ? (
        <>
          <div className="variable-tooltip-top">
            <span className="variable-tooltip-name">{`{{${tooltip.name}}}`}</span>
            <span className="variable-tooltip-value">{tooltip.value}</span>
          </div>
          <div className="variable-tooltip-meta">
            <span className="variable-tooltip-scope-badge">{tooltip.type}</span>
            {!isDynamicVariable(tooltip.name) && (
              <span className="variable-tooltip-hint">· click to edit</span>
            )}
          </div>
        </>
      ) : (
        <div className="variable-tooltip-top">
          <span className="variable-tooltip-name">{`{{${tooltip.name}}}`}</span>
          <span className="variable-tooltip-missing">Undefined</span>
        </div>
      )}
    </div>,
    document.body
  );

  return (
    <div className={`relative ${className}`}>
      {renderTextWithVariables(text)}
      {tooltipEl}
      {editingVariable && (
        <VariableEditorPopup
          editingVariable={editingVariable}
          variableContext={variableContext}
          selectedEnvironment={selectedEnvironment}
          onSave={handleSave}
          onClose={() => setEditingVariable(null)}
        />
      )}
    </div>
  );
};

export default VariableHighlighter;
